import { SimpleGrid } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { formatAbn } from '@duke-hq/shared-constants/employer';
import { SingleField } from '@duke-hq/ui/components/SingleField/SingleField';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { trpcAdmin } from '../../../lib/trpcAdmin';
import { SectionCard } from '../components';
import { useCardEdit } from '../useCardEdit';
import { DetailsEditForm } from './DetailsEditForm';
import { testIds } from '../../testIds';
import type { EmployerDetail } from '../types';

export function DetailsCard({ employer }: { employer: EmployerDetail }): React.ReactElement {
  const { isOpen, onOpen, onClose, onSuccess, onError } = useCardEdit();
  const update = trpcAdmin.workPlacements.employers.updateEmployerDetails.useMutation({
    onSuccess,
    onError,
  });

  const primaryContact = employer.contacts.find((contact) => contact.isPrimary) ?? null;
  const preferred = primaryContact?.preferredContactMethod;
  const preferredLabel = preferred === 'EMAIL' ? t`Email` : preferred === 'PHONE' ? t`Phone` : null;

  return (
    <SectionCard
      title={<Trans>Employer Details</Trans>}
      onEdit={onOpen}
      data-testid={testIds.drawerDetailsSection}
    >
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <SingleField name={<Trans>Organisation name</Trans>} value={employer.name} />
        <SingleField name={<Trans>ABN</Trans>} value={formatAbn(employer.abn)} />
        <SingleField name={<Trans>Business Type</Trans>} value={employer.businessType} />
        <SingleField name={<Trans>Preferred Contact</Trans>} value={preferredLabel} />
      </SimpleGrid>

      <FormModalWrapper
        isOpen={isOpen}
        onClose={onClose}
        modalContentHeight="auto"
        modalContent={
          <DetailsEditForm
            employer={employer}
            onSave={(payload) => update.mutate({ employerId: employer.id, ...payload })}
            onCancel={onClose}
            isSaving={update.isPending}
          />
        }
      />
    </SectionCard>
  );
}
